import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { useActivityLog } from '../../../contexts/ActivityLogContext';
import { useAuth } from '../../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import {
  Folder,
  UserCheck,
  AlertCircle,
  ArrowRight,
  Activity,
  CheckCircle,
  Flag,
  FileText,
  MessageSquare,
  Users,
  LayoutDashboard,
  Zap,
  Trophy,
} from 'lucide-react';
import { format, isAfter, formatDistanceToNow } from 'date-fns';
import { projectService } from '../../projects';
import { projectBoardService } from '../../projects/api/boardApi';
import { memberApprovalService } from '../../users';
import { competitionService } from '../../competitions/api/competitionsApi';
import MiniCalendar from '../../calendar/components/MiniCalendar';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const getTaskStatusStyles = (status) => {
  if (status === 'completed' || status === 'done') {
    return {
      backgroundColor: SYSTEM_COLORS.successTint,
      color: SYSTEM_COLORS.success
    };
  }

  if (status === 'in_progress') {
    return {
      backgroundColor: SYSTEM_COLORS.infoTint,
      color: SYSTEM_COLORS.info
    };
  }

  return {
    backgroundColor: SYSTEM_COLORS.warningTint,
    color: SYSTEM_COLORS.warning
  };
};

const getPriorityStyles = (priority) => {
  if (priority === 'high') {
    return {
      backgroundColor: SYSTEM_COLORS.dangerTint,
      color: SYSTEM_COLORS.danger
    };
  }

  if (priority === 'medium') {
    return {
      backgroundColor: SYSTEM_COLORS.warningTint,
      color: SYSTEM_COLORS.warning
    };
  }

  return {
    backgroundColor: SYSTEM_COLORS.surfaceMuted,
    color: SYSTEM_COLORS.textMuted
  };
};

const getProjectStatusStyles = (status) => {
  if (status === 'completed') {
    return {
      backgroundColor: SYSTEM_COLORS.successTint,
      color: SYSTEM_COLORS.success
    };
  }

  if (status === 'in_progress') {
    return {
      backgroundColor: SYSTEM_COLORS.infoTint,
      color: SYSTEM_COLORS.info
    };
  }

  return {
    backgroundColor: SYSTEM_COLORS.warningTint,
    color: SYSTEM_COLORS.warning
  };
};

const SurfaceCard = ({ children, className = '' }) => (
  <div
    className={`rounded-xl border ${className}`}
    style={{
      backgroundColor: SYSTEM_COLORS.shellSurface,
      borderColor: SYSTEM_COLORS.borderSoft,
    }}
  >
    {children}
  </div>
);

const SectionHeader = ({ eyebrow, title, description, action }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
    <div>
      {eyebrow && (
        <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
          {eyebrow}
        </p>
      )}
      <h2 className="mt-2 text-xl font-semibold sm:text-2xl" style={{ color: NEUTRAL_COLORS.black }}>
        {title}
      </h2>
      {description && (
        <p className="mt-2 text-sm leading-6" style={{ color: SYSTEM_COLORS.textMuted }}>
          {description}
        </p>
      )}
    </div>
    {action}
  </div>
);

const LoadingState = ({ compact = false }) => (
  <div className={`flex items-center justify-center ${compact ? 'h-28' : 'h-64'}`}>
    <div className="border-b-2 rounded-full animate-spin" style={{ height: compact ? 24 : 32, width: compact ? 24 : 32, borderColor: BRAND_COLORS.primary }} />
  </div>
);

const EmptyState = ({ title, description, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center px-6 py-10 text-center border rounded-lg" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
    <div className="flex items-center justify-center w-12 h-12 rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
      <Icon className="h-7 w-7" style={{ color: BRAND_COLORS.primary }} />
    </div>
    <p className="mt-4 text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{title}</p>
    <p className="max-w-sm mt-2 text-sm leading-6" style={{ color: SYSTEM_COLORS.textMuted }}>{description}</p>
  </div>
);

const ProjectContributions = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useApiAuth();
  
  useEffect(() => {
    const loadProjects = async () => {
      try {
        if (!user?.id) {
          setLoading(false);
          return;
        }
        
        // Get all projects
        const response = await projectService.getAllProjects();
        const allProjects = response.projects || [];
        
        // Filter projects where user is a member
        const userProjects = allProjects.filter(project => 
          project.members?.some(member => {
            const memberId = member?.user_id || member?.id;
            return memberId === user.id;
          })
        );
        
        setProjects(userProjects.slice(0, 5)); // Show top 5
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProjects();
  }, [user]);
  
  if (loading) {
    return <LoadingState />;
  }
  
  if (projects.length === 0) {
    return (
      <EmptyState
        icon={Folder}
        title="No project assignments yet"
        description="You are not assigned to any active projects at the moment. Once you are added, they will appear here with live progress."
      />
    );
  }
  
  return (
    <>
      <div className="grid gap-4 md:hidden">
        {projects.map((project) => (
          <div key={project.id} className="p-4 border rounded-lg" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{project.name}</p>
                <span className="mt-3 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={getProjectStatusStyles(project.status)}>
                  {project.status?.replace('_', ' ') || 'Active'}
                </span>
              </div>
              <div className="flex items-center justify-center rounded-md w-9 h-9" style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}>
                <Folder className="w-5 h-5" style={{ color: BRAND_COLORS.primary }} />
              </div>
            </div>
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.16em]" style={{ color: SYSTEM_COLORS.textMuted }}>
                <span>Progress</span>
                <span>{project.progress || 0}%</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                <div className="h-2.5 rounded-full" style={{ width: `${project.progress || 0}%`, backgroundColor: BRAND_COLORS.primary }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden overflow-x-auto md:block">
      <table className="min-w-full divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
          <tr>
            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase" style={{ color: SYSTEM_COLORS.textMuted }}>
              Project Name
            </th>
            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase" style={{ color: SYSTEM_COLORS.textMuted }}>
              Status
            </th>
            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase" style={{ color: SYSTEM_COLORS.textMuted }}>
              Progress
            </th>
          </tr>
        </thead>
        <tbody className="divide-y" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
          {projects.map((project) => (
            <tr key={project.id} className="transition-colors" style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{project.name}</div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={getProjectStatusStyles(project.status)}>
                  {project.status?.replace('_', ' ') || 'Active'}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="mr-2 h-2.5 w-full max-w-[120px] rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                    <div 
                      className="h-2.5 rounded-full" 
                      style={{ width: `${project.progress || 0}%`, backgroundColor: BRAND_COLORS.primary }}
                    ></div>
                  </div>
                  <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{project.progress || 0}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
};

const ApprovalNotification = ({ count }) => {
  if (count === 0) return null;

  return (
    <SurfaceCard className="p-4 sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.warningTint }}>
            <UserCheck className="w-5 h-5" style={{ color: SYSTEM_COLORS.warning }} />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>Attention Needed</p>
            <p className="mt-2 text-sm leading-6 sm:text-base" style={{ color: NEUTRAL_COLORS.black }}>
              You have {count} member{count !== 1 ? 's' : ''} waiting for approval.
            </p>
            <p className="mt-1 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
              Review onboarding requests to keep access and assignments current.
            </p>
          </div>
        </div>
        <div className="lg:pl-3">
          <Link
            to="/member-approvals"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold"
            style={{ backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning }}
          >
            <UserCheck size={16} />
            Review Now
          </Link>
        </div>
      </div>
    </SurfaceCard>
  );
};

const competitionStatusStyle = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'open':   return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
    case 'closed': return { backgroundColor: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger };
    default:       return { backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
  }
};

const CalendarSidebar = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(true);

  const canViewCompetitions = hasPermission('competitions.view');

  useEffect(() => {
    if (!canViewCompetitions) {
      setLoadingCompetitions(false);
      return;
    }
    competitionService.getAllCompetitions()
      .then(data => {
        const now = new Date();
        const upcoming = (Array.isArray(data) ? data : [])
          .filter(c => {
            if (c.status === 'closed') return false;
            if (c.applicationDeadline && !isAfter(new Date(c.applicationDeadline), now)) return false;
            return true;
          })
          .sort((a, b) => {
            if (!a.applicationDeadline) return 1;
            if (!b.applicationDeadline) return -1;
            return new Date(a.applicationDeadline) - new Date(b.applicationDeadline);
          })
          .slice(0, 5);
        setCompetitions(upcoming);
      })
      .catch(() => setCompetitions([]))
      .finally(() => setLoadingCompetitions(false));
  }, [canViewCompetitions]);

  return (
    <div className="flex flex-col gap-4">
      {/* Mini Calendar — no event dots, just date navigation */}
      <SurfaceCard className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>Calendar</p>
          <button
            type="button"
            onClick={() => navigate('/calendar')}
            className="inline-flex items-center gap-1 text-xs font-medium"
            style={{ color: BRAND_COLORS.primary }}
          >
            Open <ArrowRight size={12} />
          </button>
        </div>
        <MiniCalendar events={[]} />
      </SurfaceCard>

      {/* Upcoming Competitions — only for permitted roles */}
      {canViewCompetitions && (
        <SurfaceCard className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
              Competitions
            </p>
            <button
              type="button"
              onClick={() => navigate('/competitions')}
              className="inline-flex items-center gap-1 text-xs font-medium"
              style={{ color: BRAND_COLORS.primary }}
            >
              View All <ArrowRight size={12} />
            </button>
          </div>

          {loadingCompetitions ? (
            <LoadingState compact />
          ) : competitions.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-6">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                <Trophy className="w-5 h-5" style={{ color: SYSTEM_COLORS.textSoft }} />
              </div>
              <p className="text-xs text-center" style={{ color: SYSTEM_COLORS.textMuted }}>No open competitions right now</p>
            </div>
          ) : (
            <div className="space-y-2">
              {competitions.map(comp => {
                const deadline = comp.applicationDeadline ? new Date(comp.applicationDeadline) : null;
                return (
                  <div
                    key={comp.id}
                    className="flex items-start gap-3 p-2.5 rounded-lg border"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
                  >
                    {deadline ? (
                      <div
                        className="flex flex-col items-center justify-center flex-shrink-0 text-xs font-bold text-white rounded-md w-9 h-9"
                        style={{ backgroundColor: BRAND_COLORS.primary }}
                      >
                        <span className="leading-none">{format(deadline, 'd')}</span>
                        <span className="leading-none text-[9px] mt-0.5 opacity-80">{format(deadline, 'MMM')}</span>
                      </div>
                    ) : (
                      <div
                        className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-md"
                        style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}
                      >
                        <Trophy className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: NEUTRAL_COLORS.black }}>{comp.title}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize"
                          style={competitionStatusStyle(comp.status)}
                        >
                          {comp.status || 'open'}
                        </span>
                        {deadline && (
                          <span className="text-[10px]" style={{ color: SYSTEM_COLORS.textMuted }}>
                            due {format(deadline, 'MMM d')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SurfaceCard>
      )}
    </div>
  );
};

const TaskTable = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useApiAuth();

  useEffect(() => {
    const loadTasks = async () => {
      try {
        if (!user?.id) {
          setLoading(false);
          return;
        }

        // Get user's tasks via boardApi (same as TasksPage, returns normalized camelCase)
        const userTasks = await projectBoardService.getUserTasks();

        // Sort by createdAt and take first 5
        const recentTasks = userTasks
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);

        setTasks(recentTasks);
      } catch (error) {
        console.error('Error loading tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [user]);

  if (loading) {
    return <LoadingState compact />;
  }

  return (
    <>
      <div className="grid gap-4 md:hidden">
        {tasks.length > 0 ? (
          tasks.map((task) => (
            <button
              key={task.id}
              type="button"
              onClick={() => navigate(`/tasks/${task.id}`)}
              className="rounded-lg border p-4 text-left transition-transform hover:-translate-y-0.5"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{task.title}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.16em]" style={{ color: SYSTEM_COLORS.textMuted }}>
                    {task.dueDate ? format(new Date(task.dueDate), 'MMM d, yyyy') : 'No due date'}
                  </p>
                </div>
                <ArrowRight className="flex-shrink-0 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={getTaskStatusStyles(task.status)}>
                  {task.status?.replace('_', ' ') || 'Pending'}
                </span>
                <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={getPriorityStyles(task.priority)}>
                  {task.priority || 'Low'}
                </span>
              </div>
            </button>
          ))
        ) : (
          <EmptyState icon={AlertCircle} title="No tasks available" description="Your assigned tasks will appear here as soon as work is scheduled for you." />
        )}
      </div>

      <div className="hidden overflow-x-auto md:block">
      <table className="min-w-full divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
          <tr>
            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase" style={{ color: SYSTEM_COLORS.textMuted }}>
              Task
            </th>
            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase" style={{ color: SYSTEM_COLORS.textMuted }}>
              Due Date
            </th>
            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase" style={{ color: SYSTEM_COLORS.textMuted }}>
              Status
            </th>
            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase" style={{ color: SYSTEM_COLORS.textMuted }}>
              Priority
            </th>
          </tr>
        </thead>
        <tbody className="divide-y" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
          {tasks.length > 0 ? (
            tasks.map((task) => (
              <tr 
                key={task.id} 
                className="transition-colors cursor-pointer"
                onClick={() => navigate(`/tasks/${task.id}`)}
              >
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>{task.title}</div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                    {task.dueDate ? format(new Date(task.dueDate), 'MMM d, yyyy') : 'No due date'}
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={getTaskStatusStyles(task.status)}>
                    {task.status?.replace('_', ' ') || 'Pending'}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold" style={getPriorityStyles(task.priority)}>
                    {task.priority || 'Low'}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="px-4 py-8 text-center" style={{ color: SYSTEM_COLORS.textMuted }}>
                No tasks available
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </>
  );
};

// ── Activity helpers ──────────────────────────────────────────────────────────

const activityIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t.startsWith('task'))      return <LayoutDashboard size={14} className="text-blue-500" />;
  if (t.startsWith('milestone')) return <Flag size={14} className="text-green-500" />;
  if (t.startsWith('project'))   return <Folder size={14} className="text-indigo-500" />;
  if (t.startsWith('member'))    return <Users size={14} className="text-purple-500" />;
  if (t.startsWith('resource'))  return <Zap size={14} className="text-orange-500" />;
  if (t.includes('comment'))     return <MessageSquare size={14} className="text-yellow-500" />;
  if (t.includes('file') || t.includes('document')) return <FileText size={14} className="text-pink-500" />;
  if (t.includes('status'))      return <CheckCircle size={14} className="text-green-500" />;
  return <Activity size={14} className="text-gray-400" />;
};

const activityIconBg = (type) => {
  const t = (type || '').toLowerCase();
  if (t.startsWith('task'))      return 'bg-blue-50';
  if (t.startsWith('milestone')) return 'bg-green-50';
  if (t.startsWith('project'))   return 'bg-indigo-50';
  if (t.startsWith('member'))    return 'bg-purple-50';
  if (t.startsWith('resource'))  return 'bg-orange-50';
  if (t.includes('comment'))     return 'bg-yellow-50';
  if (t.includes('file') || t.includes('document')) return 'bg-pink-50';
  if (t.includes('status'))      return 'bg-green-50';
  return 'bg-gray-50';
};

const formatActivityDesc = (activity) => {
  if (activity.description) return activity.description;
  const d = activity.details || {};
  switch ((activity.type || '').toUpperCase()) {
    case 'TASK_CREATED':   return `Task "${d.title || 'Untitled'}" was created`;
    case 'TASK_UPDATED':   return `Task "${d.title || 'Untitled'}" was updated`;
    case 'TASK_COMPLETED': return `Task "${d.title || 'Untitled'}" was completed`;
    case 'TASK_DELETED':   return `Task "${d.title || 'Untitled'}" was deleted`;
    case 'PROJECT_CREATED': return `Project "${d.name || 'Untitled'}" was created`;
    case 'PROJECT_UPDATED': return `Project "${d.name || 'Untitled'}" was updated`;
    case 'PROJECT_DELETED': return `Project "${d.name || 'Untitled'}" was deleted`;
    case 'MEMBER_ADDED':   return d.name ? `${d.name} joined the team` : 'A new member joined';
    case 'MEMBER_REMOVED': return d.name ? `${d.name} was removed` : 'A member was removed';
    case 'RESOURCE_BOOKED':   return `Resource "${d.name || 'Unknown'}" was booked`;
    case 'RESOURCE_RETURNED': return `Resource "${d.name || 'Unknown'}" was returned`;
    case 'USER_LOGIN':  return 'User logged in';
    case 'USER_LOGOUT': return 'User logged out';
    default: {
      const human = (activity.type || 'Activity')
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      return d.title ? `${human}: ${d.title}` : d.name ? `${human}: ${d.name}` : human;
    }
  }
};

const relativeTs = (ts) => {
  if (!ts) return '';
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); }
  catch { return ''; }
};

const RecentActivity = () => {
  const { user } = useApiAuth();
  const { getRecentActivities, getUserActivities } = useActivityLog();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = isAdmin
          ? await getRecentActivities(20)
          : await getUserActivities(null, 20);
        if (!cancelled) setActivities(Array.isArray(data) ? data : []);
      } catch {
        // silently ignore — activity feed is non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isAdmin, getRecentActivities, getUserActivities]);

  return (
    <SurfaceCard className="p-5 sm:p-6">
      <SectionHeader
        title={isAdmin ? 'Recent Activity' : 'My Activity'}
        action={
          <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
            {isAdmin ? 'All team' : 'Your actions'} · last 20
          </span>
        }
      />
      <div className="mt-5">
        {loading ? (
          <LoadingState compact />
        ) : activities.length === 0 ? (
          <EmptyState
            icon={Activity}
            title="No activity yet"
            description="Actions taken across projects and tasks will appear here."
          />
        ) : (
          <div className="space-y-2">
            {activities.map((activity, idx) => (
              <div
                key={activity.id || idx}
                className="flex items-start gap-3 p-3 rounded-xl border"
                style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
              >
                <div className={`flex-shrink-0 rounded-full p-1.5 mt-0.5 ${activityIconBg(activity.type)}`}>
                  {activityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-snug" style={{ color: NEUTRAL_COLORS.black }}>
                    {formatActivityDesc(activity)}
                  </p>
                  {activity.timestamp && (
                    <p className="text-[11px] mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                      {relativeTs(activity.timestamp)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SurfaceCard>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, rolePermissions } = useApiAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';
  const canViewProjects = isAdmin || rolePermissions?.['projects.view'] === true;
  const canViewTasks = isAdmin || rolePermissions?.['tasks.view'] === true;
  const canApproveMembers = isAdmin || rolePermissions?.['admin.approveMembers'] === true;

  // Function to get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return 'Good morning';
    } else if (hour >= 12 && hour < 18) {
      return 'Good afternoon';
    } else {
      return 'Good evening';
    }
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Load pending approvals count for users allowed to approve members
        if (canApproveMembers) {
          try {
            const response = await memberApprovalService.getPendingMembers();
            setPendingCount(response.pending_members?.length || 0);
          } catch (error) {
            console.error('Error loading pending members:', error);
          }
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadDashboardData();
    }
  }, [user, canApproveMembers]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="w-8 h-8 border-b-2 rounded-full animate-spin" style={{ borderColor: BRAND_COLORS.primary }} />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {canApproveMembers && <ApprovalNotification count={pendingCount} />}

      <SurfaceCard className="overflow-hidden">
        <div className="grid gap-6 p-4 sm:px-8">
          <div>
           
            <h1 className="mt-3 text-3xl font-semibold leading-tight sm:text-3xl" style={{ color: NEUTRAL_COLORS.black }}>
              {getGreeting()}, <span style={{ color: BRAND_COLORS.accent }}>{user?.name ? user.name : 'User'} 👋</span>
            </h1>
            <p className="max-w-3xl mt-4 text-sm leading-7 sm:text-base" style={{ color: SYSTEM_COLORS.textMuted }}>
             Here's what's happening with your projects today.
            </p>

            
          </div>

          
        </div>
      </SurfaceCard>

      {(canViewTasks || canViewProjects) && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px]">
          {/* Left: Tasks + Projects stacked */}
          <div className="flex flex-col gap-6">
            {canViewTasks && (
              <SurfaceCard className="p-5 sm:p-6">
                <SectionHeader
                  
                  title="Recent Tasks"
                  
                  action={
                    <button
                      type="button"
                      onClick={() => navigate('/tasks')}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold border rounded-lg"
                      style={{ borderColor: SYSTEM_COLORS.border, color: BRAND_COLORS.primary, backgroundColor: NEUTRAL_COLORS.white }}
                    >
                      View All
                      <ArrowRight size={14} />
                    </button>
                  }
                />
                <div className="mt-6">
                  <TaskTable />
                </div>
              </SurfaceCard>
            )}

            {canViewProjects && (
              <SurfaceCard className="p-5 sm:p-6">
                <SectionHeader

                  title="My Projects"

                  action={
                    <button
                      type="button"
                      onClick={() => navigate('/projects')}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold border rounded-lg"
                      style={{ borderColor: SYSTEM_COLORS.border, color: BRAND_COLORS.primary, backgroundColor: NEUTRAL_COLORS.white }}
                    >
                      View All
                      <ArrowRight size={14} />
                    </button>
                  }
                />
                <div className="mt-6">
                  <ProjectContributions />
                </div>
              </SurfaceCard>
            )}

            <RecentActivity />
          </div>

          {/* Right: Calendar + Events */}
          <CalendarSidebar />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
